import React from 'react';
import {
  Card, CardSkeleton, ICON_ALERT_CIRCLE_OUTLINE, Message, Stack, ICON_CASH_CHECK, Dropdown, Action,
  SimpleTable,
  SimpleTableSkeleton,
} from '@doveit/bricks';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useInvoices from '../../hooks/use-invoices/useInvoices';
import ViewInvoiceTableRow from '../view-invoice-table-row/ViewInvoiceTableRow';
import ViewInvoiceCard from '../view-invoice-card/ViewInvoiceCard';
import { SortOrder } from '../../../providers/pagination';
import UpsertInvoiceAction from '../upsert-invoice-action/UpsertInvoiceAction';
import { InvoiceType } from '../../../domain/types';
import useAssignmentById from '../../../assignment/hooks/use-assignment-by-id/useAssignmentById';
import usePropertyPreview from '../../../property/hooks/use-property-preview/usePropertyPreview';
import { InvoiceReference } from '../../../providers/api/dtos';

export const INVOICES_ERROR_MESSAGE = 'Non è stato possibile recuperare i pagamenti.';
export const EMPTY_INVOICES = 'Non sono presenti pagamenti.';
export const EMPTY_MEDIATION_INVOICE = 'Non è stato ancora aggiunto un pagamento di mediazione per questo immobile.';

export type InvoicesWidgetProps = {
  canViewDetail?: boolean,
  canAdd?: boolean,
  canEdit?: boolean,
} & ({ reference: InvoiceReference; assignmentId?: never } | { assignmentId: number; reference?: never });

const InvoicesWidget: React.FC<InvoicesWidgetProps> = ({
  canViewDetail = false,
  canAdd = false,
  canEdit = false,
  reference,
  assignmentId,
}) => {
  const isMobile = useIsDevice('mobile');
  const context = reference ? 'HOLDER' : 'PROPERTY';

  const {
    data: invoices,
    isLoading: areInvoicesLoading,
    error: invoicesError,
    mutate: mutateInvoices,
  } = useInvoices({
    reference: reference ? `${reference.type}:${reference.id}` : undefined,
    assignmentId,
  }, {
    sort: {
      updatedAt: SortOrder.DESC,
    },
  });

  const { data: assignment } = useAssignmentById(assignmentId);
  const { data: property } = usePropertyPreview(assignment?.propertyId);

  const isMediationMissing = React.useMemo(() => {
    if (context === 'PROPERTY' && property && property.status === 'VENDUTO' && invoices && invoices.content.length > 0 && !invoices.content.map((invoice) => invoice.type).includes(InvoiceType.MEDIATION)) {
      return true;
    }
    return false;
  }, [context, invoices, property]);

  const disabledInvoiceFields = React.useMemo(() => ({
    property: context === 'PROPERTY',
    reference: context === 'HOLDER',
  }), [context]);

  const handleInvoicesUpdate = React.useCallback(async () => {
    await mutateInvoices();
  }, [mutateInvoices]);

  if (areInvoicesLoading) {
    return (
      <CardSkeleton aria-label="Sezione pagamenti in caricamento">
        {!isMobile && <SimpleTableSkeleton />}
      </CardSkeleton>
    );
  }

  if (invoicesError) {
    return (
      <Card>
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title="Pagamenti"
          color="critical"
        />
        <Card.Content>
          <Message message={INVOICES_ERROR_MESSAGE} />
        </Card.Content>
      </Card>
    );
  }

  return invoices && (
    <Card>
      <Card.Header
        aria-label="Sezione pagamenti (card header)"
        title="Pagamenti"
        icon={{ path: ICON_CASH_CHECK }}
        primaryActions={[
          (!isMobile && canAdd && (
            <UpsertInvoiceAction
              aria-label="Pulsante per aggiungere un nuovo pagamento"
              reference={reference}
              assignmentId={assignmentId}
              onSuccess={handleInvoicesUpdate}
              disabledFields={disabledInvoiceFields}
            >
              {({ openUpsertInvoiceModal }) => (
                <Action
                  aria-label="Pulsante per aggiungere un nuovo pagamento"
                  label="Aggiungi"
                  size="S"
                  onClick={openUpsertInvoiceModal}
                />
              )}
            </UpsertInvoiceAction>
          )),
        ]}
        secondaryActions={[
          (isMobile && canAdd && (
            <UpsertInvoiceAction
              aria-label="Pulsante per aggiungere un nuovo pagamento"
              reference={reference}
              assignmentId={assignmentId}
              onSuccess={handleInvoicesUpdate}
              disabledFields={disabledInvoiceFields}
            >
              {({ openUpsertInvoiceModal }) => (
                <Dropdown.Option
                  aria-label="Opzione per aggiungere un nuovo pagamento"
                  label="Aggiungi"
                  onClick={openUpsertInvoiceModal}
                />
              )}
            </UpsertInvoiceAction>
          )),
        ]}
      />
      <Card.Content>
        <Stack aria-label="Lista pagamenti">
          {isMediationMissing && (
            <Message type="warning" message={EMPTY_MEDIATION_INVOICE} />
          )}
          {isMobile && invoices.content.length > 0 && (
            invoices.content.map((invoice) => (
              <ViewInvoiceCard
                key={invoice.id}
                invoice={invoice}
                context={context}
                canView={canViewDetail}
                canEdit={canEdit}
                onEdit={mutateInvoices}
              />
            ))
          )}
          {!isMobile && invoices.content.length > 0 && (
            <SimpleTable>
              <SimpleTable.Body>
                {invoices.content.map((invoice) => (
                  <SimpleTable.Row asChild key={invoice.id}>
                    <ViewInvoiceTableRow
                      invoice={invoice}
                      context={context}
                      canView={canViewDetail}
                      canEdit={canEdit}
                      onEdit={mutateInvoices}
                    />
                  </SimpleTable.Row>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
          )}
          {invoices.content.length === 0 && (
            <Message message={EMPTY_INVOICES} />
          )}
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default InvoicesWidget;
